import React from 'react';
import {ProfileFeatureCard} from '../constants';
import styles from '../Profile.module.css';

interface ProfileFeaturesSectionProps {
  features: ProfileFeatureCard[];
  onDirectMessagingClick?: () => void;
  onPostCommentsClick?: () => void;
  onFollowSystemClick?: () => void;
  onMediaUploadsClick?: () => void;
  onTalentSearchClick?: () => void;
  onSafetyFeaturesClick?: () => void;
}

const socialFeaturesIconStyle = {
  '--profile-information-icon-url': `url("${process.env.PUBLIC_URL}/icons/user-group.svg")`,
} as React.CSSProperties;

const featureIconStyle = (fileName: string) => ({
  '--profile-feature-icon-url': `url("${process.env.PUBLIC_URL}/icons/${fileName}")`,
}) as React.CSSProperties;

const FeatureTitle: React.FC<{feature: ProfileFeatureCard}> = ({feature}) => (
  <strong className={styles.featureTitle}>
    {feature.icon ? (
      <span
        className={styles.featureTitleIcon}
        style={featureIconStyle(feature.icon)}
        aria-hidden="true"
      />
    ) : null}
    <span>{feature.title}</span>
  </strong>
);

const ProfileFeaturesSection: React.FC<ProfileFeaturesSectionProps> = ({
  features,
  onDirectMessagingClick,
  onPostCommentsClick,
  onFollowSystemClick,
  onMediaUploadsClick,
  onTalentSearchClick,
  onSafetyFeaturesClick,
}) => (
  <section
    className={`${styles.profileSection} ${styles.profileInformationSection}`}
    aria-labelledby="profile-social-features-title"
  >
    <div className={styles.profileInformationHeader}>
      <div className={styles.profileInformationHeading}>
        <div className={styles.profileInformationTitleRow}>
          <span className={styles.profileInformationHeadingIconTile} aria-hidden="true">
            <span
              className={styles.profileInformationHeadingIcon}
              style={socialFeaturesIconStyle}
            />
          </span>
          <h3
            id="profile-social-features-title"
            className={`${styles.sectionTitle} ${styles.profileInformationTitle}`}
          >
            Social Features Available
          </h3>
        </div>
        <p className={styles.profileInformationSubtitle}>
          Connect, share, and engage with the XPSR community.
        </p>
      </div>
    </div>

    <div className={styles.servicesExperiencesDivider} aria-hidden="true" />

    <div className={styles.featuresGrid}>
      {features.map((feature) => {
        const onFeatureClick = feature.id === 'direct-messaging'
          ? onDirectMessagingClick
          : feature.id === 'post-comments'
            ? onPostCommentsClick
            : feature.id === 'follow-system'
              ? onFollowSystemClick
              : feature.id === 'media-uploads'
                ? onMediaUploadsClick
                : feature.id === 'talent-search'
                  ? onTalentSearchClick
                  : feature.id === 'safety-features'
                    ? onSafetyFeaturesClick
                    : undefined;

        if (onFeatureClick) {
          return (
            <button
              key={feature.id}
              type="button"
              className={`${styles.featureCard} ${styles.featureCardButton}`}
              onClick={onFeatureClick}
            >
              <FeatureTitle feature={feature} />
              <p className={styles.featureDescription}>{feature.description}</p>
            </button>
          );
        }

        return (
          <div key={feature.id} className={styles.featureCard}>
            <FeatureTitle feature={feature} />
            <p className={styles.featureDescription}>{feature.description}</p>
          </div>
        );
      })}
    </div>
  </section>
);

export default React.memo(ProfileFeaturesSection);
